import React, { useState } from "react";
import PageBanner from "../components/PageBanner";
import { HERO_IMAGES, COMPANY } from "../mock";
import { createLead } from "../api";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    try {
      await createLead({ type: "contact", ...form });
      toast({ title: "Message sent", description: "Thank you for getting in touch. We will reply as soon as possible." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again shortly.", variant: "destructive" });
    }
  };

  const info = [
    { icon: Phone, label: "Telephone", value: COMPANY.phone, href: `tel:${COMPANY.phone}` },
    { icon: Mail, label: "Email", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: "Address", value: COMPANY.address },
    { icon: Clock, label: "Opening Hours", value: "Mon–Sat: 9am – 6pm · Sun: By appointment" },
  ];

  return (
    <div className="bg-white">
      <PageBanner title="Contact Us" subtitle="We would be delighted to hear from you — do get in touch." image={HERO_IMAGES[0]} />
      <div className="max-w-6xl mx-auto px-6 py-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-serif-display text-3xl font-bold gpm-text-green mb-4">Get in Touch</h2>
          <div className="gpm-divider mb-8" />
          <div className="space-y-6">
            {info.map((i) => {
              const Icon = i.icon;
              const inner = (
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 gpm-bg-green">
                    <Icon size={20} className="gpm-text-gold" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">{i.label}</div>
                    <div className="gpm-text-green font-medium">{i.value}</div>
                  </div>
                </div>
              );
              return i.href ? (
                <a key={i.label} href={i.href} className="block hover:opacity-80 transition-opacity">{inner}</a>
              ) : (
                <div key={i.label}>{inner}</div>
              );
            })}
          </div>
          <div className="mt-10 overflow-hidden border border-black/10">
            <iframe
              title="map"
              src="https://www.openstreetmap.org/export/embed.html?bbox=-4.30%2C50.55%2C-3.20%2C51.00&layer=mapnik&marker=50.7772%2C-3.7477"
              className="w-full h-64"
              loading="lazy"
            />
          </div>
        </div>

        <div className="p-8" style={{ backgroundColor: "#f4f2ea" }}>
          <h3 className="font-serif-display text-2xl font-bold gpm-text-green mb-6">Send a Message</h3>
          <form onSubmit={submit} className="space-y-4">
            <input required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm bg-white" />
            <input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm bg-white" />
            <input placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-3 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm bg-white" />
            <textarea rows={6} required placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 border border-black/15 outline-none focus:border-[var(--gpm-green)] text-sm bg-white" />
            <button type="submit" className="flex items-center justify-center gap-2 w-full py-4 gpm-text-cream text-sm font-semibold uppercase tracking-wider transition-transform hover:-translate-y-0.5" style={{ backgroundColor: "var(--gpm-green)" }}>
              <Send size={16} /> Send Message
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
